import { useEffect, useRef, useState } from "react";

/**
 * useDelayedReveal — mirrors an incoming "called" list but promotes each
 * new entry to the "revealed" set only after `delayMs`. Existing entries
 * are treated as already-revealed on mount so a page reload never leaves
 * called cells dark.
 *
 * Rationale: the standard reveal sequence takes ~2.35s from ball spawn to
 * magic-trail landing. The board should stay quiet until the trail
 * arrives, then light up dramatically. This hook powers that timing on
 * top of the existing store — no need to slow down the sync or the store.
 */
export function useDelayedReveal(called, delayMs = 2350) {
  const [revealed, setRevealed] = useState(() =>
    new Set(called.map((c) => c.value)),
  );
  const seenRef = useRef(new Set(called.map((c) => c.value)));
  const timersRef = useRef([]);

  useEffect(() => {
    called.forEach((c) => {
      if (!seenRef.current.has(c.value)) {
        seenRef.current.add(c.value);
        const id = window.setTimeout(() => {
          setRevealed((prev) => {
            if (prev.has(c.value)) return prev;
            const next = new Set(prev);
            next.add(c.value);
            return next;
          });
        }, delayMs);
        timersRef.current.push(id);
      }
    });
  }, [called, delayMs]);

  useEffect(
    () => () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    },
    [],
  );

  return revealed;
}
